import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import DateFilter from './DateFilter';
import { hasActiveGlobalFilters, defaultGlobalFilters, sameText, uniqueNormalized, spocsFromCompanies } from '../utils/filterUtils';

const API = process.env.REACT_APP_BACKEND_API_URL;

const selectStyle = {
    padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db',
    fontSize: '13px', background: '#fff', minWidth: '200px'
};
const labelStyle = { fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px', display: 'block' };

// Props:
//   filters / onChange  — controlled state (pass a React setState for onChange)
//   rows                — the page's own rows, used so options always include
//                         values actually present in the data
//   show                — { company, spoc, dates }; any can be turned off when a
//                         page already renders that control itself
//   resetSignal         — bump to force the date filters to visually reset
//   accessors           — how to read company/spoc off a row (defaults handle
//                         both jobs' company_name and tasks'/candidates' company)
const GlobalFilters = ({
    filters,
    onChange,
    rows = [],
    show = { company: true, spoc: true, dates: true },
    accessors = {},
    resetSignal = 0,
    dateDefault = 'today' // 'all' on Interviews / Candidate Details
}) => {
    const [companies, setCompanies] = useState([]);
    // Bumped on Clear so the DateFilters remount and visibly reset (they only
    // read initialValue on mount).
    const [resetKey, setResetKey] = useState(0);

    useEffect(() => {
        fetch(`${API}/api/companies`)
            .then(res => res.json())
            .then(data => setCompanies(Array.isArray(data) ? data : []))
            .catch(err => console.error('Failed to fetch companies:', err));
    }, []);

    const companyOf = accessors.company || ((r) => r.company ?? r.company_name);
    const spocOf = accessors.spoc || ((r) => r.client_spoc);

    // Company Master is the source of truth; anything already present in the
    // page's data is unioned in so a filter never hides a real row's value.
    const companyOptions = useMemo(
        () => uniqueNormalized(companies.map(c => c.short_name), rows.map(r => companyOf(r))),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [companies, rows]
    );

    // Client SPOC options come from the same /api/companies response (each company carries
    // its SPOCs), scoped to the selected company, then unioned with whatever this page's own
    // rows contain. De-duplicated case/whitespace-insensitively, master spelling first.
    const spocOptions = useMemo(() => {
        const fromRows = rows
            .filter(r => !filters.company || sameText(companyOf(r), filters.company))
            .map(r => spocOf(r));
        return uniqueNormalized(spocsFromCompanies(companies, filters.company), fromRows);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companies, rows, filters.company]);

    const active = hasActiveGlobalFilters(filters, dateDefault);

    const handleClear = () => {
        onChange(defaultGlobalFilters(dateDefault));
        setResetKey(k => k + 1);
    };

    return (
        <div style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px',
            padding: '12px 14px', marginBottom: '16px', boxSizing: 'border-box'
        }}>
            {(show.company || show.spoc) && (
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    {show.company && (
                        <div>
                            <label style={labelStyle}>Company Name</label>
                            <select
                                style={selectStyle}
                                value={filters.company}
                                onChange={(e) => onChange(prev => ({ ...prev, company: e.target.value, spoc: '' }))}
                            >
                                <option value="">All Companies</option>
                                {companyOptions.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    )}
                    {show.spoc && (
                        <div>
                            <label style={labelStyle}>Company SPOC Name</label>
                            <select
                                style={selectStyle}
                                value={filters.spoc}
                                onChange={(e) => onChange(prev => ({ ...prev, spoc: e.target.value }))}
                            >
                                <option value="">All SPOCs</option>
                                {spocOptions.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    )}
                    {active && (
                        <button
                            type="button"
                            onClick={handleClear}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '4px', background: '#fee2e2',
                                color: '#b91c1c', border: 'none', padding: '9px 12px', borderRadius: '6px',
                                fontSize: '12px', fontWeight: '600', cursor: 'pointer'
                            }}
                        >
                            <X size={12} /> Clear filters
                        </button>
                    )}
                </div>
            )}

            {show.dates && (
                // Received Date and Action Date, side by side (wrapping on narrow screens).
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: (show.company || show.spoc) ? '12px' : 0 }}>
                    <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                        <label style={labelStyle}>Received Date</label>
                        <DateFilter
                            key={`received-${resetKey}-${resetSignal}`}
                            dateField="date_received"
                            initialValue={filters.receivedDate}
                            onFilterChange={(v) => onChange(prev => ({ ...prev, receivedDate: v }))}
                        />
                    </div>
                    <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                        <label style={labelStyle}>Action Date</label>
                        <DateFilter
                            key={`action-${resetKey}-${resetSignal}`}
                            dateField="action_date"
                            initialValue={filters.actionDate}
                            onFilterChange={(v) => onChange(prev => ({ ...prev, actionDate: v }))}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default GlobalFilters;